import { useEffect } from "react";
import { useUploadStore, hasEditorImportAdmission } from "../../store/uploadStore";
import { apiService } from "../../services/apiService";
import { cancelJob } from "../../services/api/jobs";
import { readLibraryAttachResumePayload } from "../../services/editor/editorLibraryAttachResume";
import { hasUploadRuntimeControl } from "../../utils/uploadRuntimeControl";
import { canResumeEditorImportWithoutOriginalFile } from "../../utils/upload/editorImportResume";

/** Read back the SAME known Job. Network failure, 404, timeout and optimistic
 * cancel never release admission and never create/re-submit remote work. */
export async function reconcileEditorImportItem(id: string, token: string): Promise<void> {
  const initial = useUploadStore.getState().items[id];
  const admission = initial?.editorImportAdmission;
  if (!initial?.jobId || admission?.state !== "reconciling") return;
  // Runtime Cancel needs its own exact cleanup ACK. Converter completion must
  // not revive an import the user has already cancelled in Rendering.
  if (initial.cancelled && admission.runtimeStarted) return;
  const jobId = initial.jobId;
  const current = () => {
    const item = useUploadStore.getState().items[id];
    return item?.jobId === jobId && item.editorImportAdmission?.executionId === admission.executionId && hasEditorImportAdmission(item) ? item : null;
  };
  if (initial.cancelled) {
    await cancelJob(jobId, token).catch(() => undefined);
    if (!current()) return;
  }
  const result = await apiService.getConversionJobStatus(jobId, token) as Record<string, unknown> | null;
  const item = current();
  if (!item || !result || (result.job_id && result.job_id !== jobId)) return;
  const status = String(result.status ?? "").toLowerCase();
  const store = useUploadStore.getState();
  if (["failed", "cancelled", "attach_vse_failed"].includes(status)) {
    store.releaseEditorImport({ id, jobId, executionId: admission.executionId, reason: "remote_terminal" });
    if (status !== "cancelled") {
      store.upsertItem({
        id,
        conversionFailure: {
          source: "converter",
          status: "failed",
          stage:
            item.status === "optimizing"
              ? "optimizing"
              : item.status === "uploading" || item.status === "uploaded"
                ? "uploading"
                : "converting",
          fileName: item.fileName,
        },
      });
      store.fail({ id, error: String(result.error_message || "Import failed.") });
    }
    return;
  }
  const completed = ["completed", "success", "attach_vse_completed"].includes(status);
  if (item.flow === "editor_library_attach") {
    if (!completed) return;
    const resume = readLibraryAttachResumePayload(item);
    if (!resume) return;
    store.upsertItem({ id, cancelled: false, status: "converting", editorImportRuntimeState: "awaiting_user_restore",
      fileData: { ...item.fileData, libraryAttachResume: { ...resume, phase: "attach_complete" } } });
    store.updateEditorImportAdmission(id, admission.executionId, { state: "held", remotePhase: "accepted" });
    return;
  }
  if (item.cancelled && !completed) return;
  if (completed && item.cancelled) {
    const path = typeof result.converted_file_path === "string" ? result.converted_file_path : null;
    const url = typeof result.download_public_url === "string" ? result.download_public_url : typeof result.download_signed_url === "string" ? result.download_signed_url : null;
    if (!path && !url) return;
    store.upsertItem({ id, cancelled: false, status: "rendering", progress: 100, gcsPath: path, fileUrl: url, convertedDownloadUrl: url, editorImportRuntimeState: "awaiting_user_restore" });
  }
  if (completed || ["queued", "converting", "optimizing", "processing", "running"].includes(status))
    store.updateEditorImportAdmission(id, admission.executionId, { state: "held", remotePhase: "accepted" });
}

export function useEditorImportReconciliation(token: string | null | undefined): void {
  useEffect(() => {
    if (!token) return;
    let disposed = false;
    const inFlight = new Set<string>();
    // Persisted pre-submit File work cannot reconstruct a File after hard reload.
    // 서버측 입력이 이미 있는 항목(Library import 등)은 원본 File이 필요하지 않으므로
    // 이 sweep이 실패로 종결하지 않는다. (canResumeEditorImportWithoutOriginalFile)
    Object.values(useUploadStore.getState().items).forEach((item) => {
      if (item.flow === "editor_object_import" && hasEditorImportAdmission(item) &&
          item.editorImportAdmission?.remotePhase === "not_submitted" &&
          !hasUploadRuntimeControl(item.id) &&
          !canResumeEditorImportWithoutOriginalFile(item)) {
        useUploadStore.getState().releaseEditorImport({ id: item.id, executionId: item.editorImportAdmission.executionId, reason: "pre_submit_failure" });
        useUploadStore.getState().fail({ id: item.id, error: "File is no longer available. Select the object again." });
      }
    });
    const tick = () => {
      if (disposed) return;
      Object.values(useUploadStore.getState().items).forEach((item) => {
        if (item.editorImportAdmission?.state !== "reconciling" || !item.jobId || inFlight.has(item.id)) return;
        inFlight.add(item.id);
        void reconcileEditorImportItem(item.id, token).catch(() => undefined).finally(() => inFlight.delete(item.id));
      });
    };
    tick();
    const timer = window.setInterval(tick, 2000);
    return () => { disposed = true; window.clearInterval(timer); };
  }, [token]);
}
